import React, { useState, useEffect } from "react";
import { auth, functions } from "../../firebase/config";
import { httpsCallable } from "firebase/functions";
import { HiMoon } from "react-icons/hi2";

interface SleepData {
  hypnogramSamples: Array<{
    timestamp: string;
    level: number;
  }>;
  totalDurationAsleep: number;
  sleepEfficiency: number;
  sleepStartTs: string | any;
  sleepEndTs: string | any;
  totalDurationInBed: number;
  totalDurationAsleepHours: number;
  totalDurationInBedHours: number;
  rhr?: number;
  hrv?: number;
  respRate?: number;
}

interface SleepSegment {
  start: Date;
  end: Date;
  level: number;
  duration: number; // in minutes
}

interface HypnogramSample {
  timestamp: string;
  level: number;
}

interface SleepTimelineProps {
  dateID: string; // YYYYMMDD format
  className?: string;
  currentDay?: any;
  sleepTimeline?: HypnogramSample[]; // Optional pre-loaded sleep timeline data
}

const SleepTimeline: React.FC<SleepTimelineProps> = ({
  dateID,
  className = "",
  currentDay,
  sleepTimeline,
}) => {
  const [sleepData, setSleepData] = useState<SleepData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert dateID (YYYYMMDD) to date format (YYYY-MM-DD) for the PREVIOUS day
  const formatDateForAPI = (dateID: string): string => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));

    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() - 1);

    const prevYear = date.getFullYear();
    const prevMonth = String(date.getMonth() + 1).padStart(2, "0");
    const prevDay = String(date.getDate()).padStart(2, "0");

    return `${prevYear}-${prevMonth}-${prevDay}`;
  };

  // Sleep level mapping and colors
  const getSleepStageInfo = (level: number) => {
    switch (level) {
      case 1:
        return {
          name: "Light",
          color: "bg-neutral",
          lightColor: "bg-neutral",
        };
      case 2:
        return {
          name: "Deep",
          color: "bg-purple-800",
          lightColor: "bg-purple-800",
        };
      case 4:
        return {
          name: "REM",
          color: "bg-purple-300",
          lightColor: "bg-purple-300",
        };
      case 5:
        return {
          name: "Awake",
          color: "bg-purple-900",
          lightColor: "bg-purple-900",
        };
      case 6:
        return {
          name: "REM",
          color: "bg-purple-700",
          lightColor: "bg-purple-700",
        };
      default:
        return {
          name: "Unknown",
          color: "bg-base-300",
          lightColor: "bg-base-300",
        };
    }
  };

// Process hypnogram samples into segments
//
// GUARANTEES:
// 1. Old behavior is preserved 100%
// 2. If no valid end timestamp is provided → result is IDENTICAL to old code
// 3. Only adds the missing last segment when end time is known
const processHypnogramData = (
  samples: HypnogramSample[],
  explicitSleepEndTs?: number // OPTIONAL, may be missing / 0 / invalid
): SleepSegment[] => {
  if (samples.length === 0) return [];

  const segments: SleepSegment[] = [];

  // --------------------------------------------
  // ✅ ORIGINAL LOGIC (UNCHANGED)
  // --------------------------------------------
  // Each segment is calculated from current → next sample
  // This is EXACTLY how your old code worked
  for (let i = 0; i < samples.length - 1; i++) {
    const current = samples[i];
    const next = samples[i + 1];

    const start = new Date(current.timestamp);
    const end = new Date(next.timestamp);
    const duration = (end.getTime() - start.getTime()) / (1000 * 60);

    segments.push({
      start,
      end,
      level: current.level,
      duration,
    });
  }

  // --------------------------------------------
  // ➕ NEW LOGIC (SAFE EXTENSION)
  // --------------------------------------------
  // WHY:
  // - Hypnogram does NOT include an explicit "end" sample
  // - So the final sleep stage duration is lost
  //
  // SAFETY RULES:
  // - Runs ONLY if we have a VALID end timestamp
  // - If NOT valid → function output is IDENTICAL to old code
  // - No mutation of existing segments
  if (
    typeof explicitSleepEndTs === "number" &&
    explicitSleepEndTs > 0
  ) {
    const lastSample = samples[samples.length - 1];
    const start = new Date(lastSample.timestamp);
    const end = new Date(explicitSleepEndTs);

    // Extra guard:
    // - Prevents negative / zero-length segments
    // - Protects against corrupted provider data
    if (end.getTime() > start.getTime()) {
      segments.push({
        start,
        end,
        level: lastSample.level,
        duration: (end.getTime() - start.getTime()) / (1000 * 60),
      });
    }
  }

  return segments;
};



  // Calculate sleep stage durations
  const calculateStageDurations = (segments: SleepSegment[]) => {
    const durations = { 1: 0, 2: 0, 4: 0, 5: 0, 6: 0 };

    segments.forEach((segment) => {
      if (durations.hasOwnProperty(segment.level)) {
        durations[segment.level as keyof typeof durations] += segment.duration;
      }
    });

    const combinedDurations = {
      1: durations[1],
      2: durations[2],
      4: durations[4], // + durations[6], // REM sleep (combined levels 4 and 6)
      5: durations[5],
      6: durations[6], // + durations[4], // REM sleep (same as level 4, for display consistency)
    };

    return combinedDurations;
  };

  // Format time for display
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);

    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  };

  useEffect(() => {
    const fetchSleepData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If sleepTimeline is provided, use it directly for faster loading
        if (sleepTimeline && sleepTimeline.length > 0) {
          console.log("Using provided sleepTimeline data for faster loading");

          // Calculate basic metrics from the timeline
          // const segments = processHypnogramData(sleepTimeline);
          const segments = processHypnogramData(
            sleepData?.hypnogramSamples ? sleepData : currentDay.sleepTimeline,
            currentDay.sleepEndTs ? currentDay?.sleepEndTs : 0
          );
          const totalDuration = segments.reduce(
            (sum, segment) => sum + segment.duration,
            0
          );
          console.log("total duration we have ", totalDuration)
          const mockSleepData: SleepData = {
            hypnogramSamples: sleepTimeline,
            totalDurationAsleep: totalDuration * 60, // Convert to seconds
            sleepEfficiency: 0, // Will be calculated from segments if needed
            totalDurationInBed: totalDuration * 60, // Approximation
            totalDurationAsleepHours: totalDuration / 60,
            sleepStartTs: 0,
            sleepEndTs: 0,
            totalDurationInBedHours: totalDuration / 60,
            // Health metrics not available from timeline alone
            rhr: undefined,
            hrv: undefined,
            respRate: undefined,
          };

          setSleepData(mockSleepData);
          setLoading(false);
          return;
        }

        // Fallback to API call if no sleepTimeline provided
        if (!auth.currentUser) {
          setLoading(false);
          return;
        }

        const getUserSleepData = httpsCallable(functions, "getUserSleepData");
        const date = formatDateForAPI(dateID);
        const result = await getUserSleepData({ date });

        const response = result.data as {
          success: boolean;
          error: string | null;
          data: SleepData | null;
        };

        if (response.success && response.data) {
          setSleepData(response.data);
        } else {
          setSleepData(null);
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching sleep data:", err);
        setError("Failed to fetch sleep data");
      } finally {
        setLoading(false);
      }
    };

    fetchSleepData();
  }, [dateID, sleepTimeline]);

  
  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-3">
          <HiMoon className="w-4 h-4 text-base-content/60" />
          <span className="text-sm font-medium text-base-content/80">
            Loading sleep timeline...
          </span>
        </div>
        <div className="space-y-3">
          {/* Animated loading skeleton */}
          {["Awake", "REM", "Light", "Deep"].map((stageName, index) => (
            <div key={stageName} className="space-y-1">
              <div className="flex items-center gap-2">
                <div
                  className="w-12 h-3 bg-base-300 rounded animate-pulse"
                  style={{ animationDelay: `${index * 0.1}s` }}
                ></div>
                <div
                  className="w-16 h-3 bg-base-300 rounded animate-pulse"
                  style={{ animationDelay: `${index * 0.1 + 0.05}s` }}
                ></div>
              </div>
              <div className="relative h-6 bg-base-200 rounded-md overflow-hidden">
                <div
                  className="absolute top-0 h-full bg-gradient-to-r from-transparent via-base-100 to-transparent opacity-50 animate-pulse"
                  style={{
                    width: `${60 + Math.random() * 30}%`,
                    left: `${Math.random() * 20}%`,
                    animationDelay: `${index * 0.2}s`,
                    animationDuration: "1.5s",
                  }}
                />
                <div
                  className="absolute top-0 h-full bg-base-300 rounded opacity-60"
                  style={{
                    width: `${40 + Math.random() * 40}%`,
                    left: `${Math.random() * 30}%`,
                    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    animationDelay: `${index * 0.3}s`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Loading progress indicator */}
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-1 bg-base-300 rounded-full overflow-hidden">
            <div className="h-full bg-purple-500 rounded-full animate-pulse transition-all duration-1000"></div>
          </div>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-3">
          <HiMoon className="w-4 h-4 text-red-600" />
          <span className="text-sm font-medium text-red-700">
            Error loading sleep timeline
          </span>
        </div>
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  if (!sleepData) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-3">
          <HiMoon className="w-4 h-4 text-base-content/70" />
          <span className="text-sm font-medium text-base-content/80">
            Sleep Timeline
          </span>
        </div>
        <div className="text-sm text-base-content/60 text-center py-4">
          No sleep timeline data available for this date
        </div>
      </div>
    );
  }

  // const segments = processHypnogramData(sleepData.hypnogramSamples);
  const segments = processHypnogramData(
            sleepData.hypnogramSamples,
            currentDay.sleepEndTs ? currentDay?.sleepEndTs : 0
          );
  console.log("segments we got ", segments)        
  const stageDurations = calculateStageDurations(segments);
  const totalDuration = segments.reduce(
    (sum, segment) => sum + segment.duration,
    0
  );
  const sleepStart = segments.length > 0 ? segments[0].start : null;
  const sleepEnd =
    segments.length > 0 ? segments[segments.length - 1].end : null;
console.log("the sleepData we have ", currentDay)
const resolvedSleepStart = currentDay?.sleepStartTs
  ? new Date(currentDay.sleepStartTs)
  : sleepStart ?? null;

const resolvedSleepEnd = currentDay?.sleepEndTs
  ? new Date(currentDay.sleepEndTs)

  : sleepEnd ?? null;
  if (segments.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 mb-3">
          <HiMoon className="w-4 h-4 text-base-content/70" />
          <span className="text-sm font-medium text-base-content/80">
            Sleep Timeline
          </span>
        </div>
        <div className="text-sm text-base-content/60 text-center py-4">
          No sleep data available for this date from your device
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <HiMoon className="w-4 h-4 text-base-content/60" />
        <span className="text-sm font-medium text-base-content/80 font-heading">
          Sleep Timeline
        </span>
      </div>

      {/* Sleep period header */}
      <div className="text-center mb-4">
        <h4 className="text-lg font-semibold text-base-content">
          {resolvedSleepStart &&
            resolvedSleepEnd &&
            `${formatTime(resolvedSleepStart)} – ${formatTime(resolvedSleepEnd)}`}
        </h4>
        <p className="text-sm text-base-content/70">Sleep timeline</p>
      </div>

      {/* Sleep stage summary */}
      <div className="grid grid-cols-2 gap-2 mb-6 text-xs">
        {[
          { level: 1, name: "Awake" },
          { level: 6, name: "REM" },
          { level: 4, name: "Light" },
          { level: 5, name: "Deep" },
          { level: 2, name: "Sleeping" },
        ].map((stage) => {
          const duration =
            stageDurations[stage.level as keyof typeof stageDurations];
          const stageInfo = getSleepStageInfo(stage.level);

          if (duration === 0 || !stageInfo) return null;

          return (
            <div key={stage.level} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-sm ${stageInfo.color}`}></div>
              <span className="text-base-content/80">
                <span className="font-medium">{stage.name}</span>
                <span className="text-base-content/60 ml-1">
                  • {formatDuration(duration)}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Timeline visualization */}
      <div className="space-y-4">
        {[
          { level: 1, name: "Awake" },
          { level: 6, name: "REM" },
          { level: 4, name: "Light" },
          { level: 5, name: "Deep" },
          { level: 2, name: "Sleeping" },
        ].map((stage) => {
          const stageSegments = segments.filter((s) => s.level === stage.level);
          const stageInfo = getSleepStageInfo(stage.level);

          if (stageSegments.length === 0 || !stageInfo) return null;

          return (
            <div key={stage.level} className="relative">
              <div className="flex items-center mb-1">
                <span className="text-xs font-medium text-base-content/80 w-12">
                  {stage.name}
                </span>
              </div>

              <div className="relative h-6 bg-base-200 rounded-md overflow-hidden">
                {stageSegments.map((segment, index) => {
                  const startPercent =
                    ((segment.start.getTime() - (sleepStart?.getTime() || 0)) /
                      (totalDuration * 60 * 1000)) *
                    100;
                  const widthPercent = (segment.duration / totalDuration) * 100;

                  return (
                    <div
                      key={index}
                      className={`absolute top-0 h-full ${stageInfo.color} opacity-80`}
                      style={{
                        left: `${Math.max(0, startPercent)}%`,
                        width: `${widthPercent}%`,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time markers */}
      {sleepStart && sleepEnd && (
        <div className="relative mt-4 h-8">
          <div className="absolute inset-x-0 top-2 border-t border-base-300"></div>
          <div className="relative flex justify-between text-xs text-base-content/70">
            <span className="bg-base-100 px-1">{formatTime(sleepStart)}</span>
            <span className="bg-base-100 px-1">
              {formatTime(
                new Date((sleepStart.getTime() + sleepEnd.getTime()) / 2)
              )}
            </span>
            <span className="bg-base-100 px-1">{formatTime(sleepEnd)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SleepTimeline;
